import { Order, Table, Waiter, Access, User, Content, Avatar, OrgCategory, Offer } from "@/types";
import { z } from "zod";
import { IndustryId } from "@/constants/industries";
import { Website } from "./website";
import { Quote } from "./quote";
import { Branch, Schedule } from "./branch";
import { PlanType } from "./plans";
import { LoyaltyProgram } from "./loyalty";
import { BankIcon, CallIncoming04Icon, Cash02Icon, CreditCardPosIcon, RestaurantTableIcon, SafeDelivery01Icon, Scooter03Icon, SmartPhone01Icon, WhatsappIcon } from "@hugeicons/core-free-icons";
import { MENU_FLOWS, ORDER_SOURCES } from "@/constants/global";
import { Legal } from "./legal";

export const InvitationSchema = z.object({
  id: z.string().nullable(),
  email: z.string().email(),
  role: z.enum(["admin", "manager", "rider"]),
  status: z.enum(["pending", "accepted", "rejected", "failed"]).nullable(),
});

export type Invitation = z.infer<typeof InvitationSchema>;


// SupportedPaymentMethods
export enum PaymentMethodsType {
  CASH = "CASH",
  CREDIT_CARD = "CREDIT_CARD",
  BANK_TRANSFER = "BANK_TRANSFER",
}

export type POSType = 'bac' | 'banpro' | 'ficohsa' | 'lafise';

export type CreditCard = "visa" | "american-express" | "mastercard" | "discover" | "dinners-club";

export interface Bank {
  accountNumber: string;
  bankName: string;
  currency: string;
}

export interface Cash {
  amount: number;
}

export interface PaymentMethods {
  cash?: boolean | Cash;
  creditCard?: CreditCard[] | null;
  bankTransfer?: Bank[] | null;
  stripeEnabled?: boolean;
}

export const PaymentMethodsLabels = {
  [PaymentMethodsType.CASH]: "Efectivo",
  [PaymentMethodsType.CREDIT_CARD]: "Tarjeta",
  [PaymentMethodsType.BANK_TRANSFER]: "Transferencia",
};

export const PaymentMethodsIcons = {
  [PaymentMethodsType.CASH]: Cash02Icon,
  [PaymentMethodsType.CREDIT_CARD]: CreditCardPosIcon,
  [PaymentMethodsType.BANK_TRANSFER]: BankIcon,
};

export const DineMoneIcons = {
  [MENU_FLOWS.DINE_IN]: RestaurantTableIcon,
  [MENU_FLOWS.DELIVERY]: Scooter03Icon,
  [MENU_FLOWS.PICK_UP]: SafeDelivery01Icon,
};

export const OrderSourceIcons = {
  [ORDER_SOURCES.WHATSAPP]: WhatsappIcon,
  [ORDER_SOURCES.CALL]: CallIncoming04Icon,
  [ORDER_SOURCES.BITELINK]: SmartPhone01Icon,
}

export interface FeeConfig {
  type: "fixed" | "percentage";
  value?: number;
  minValue?: number;
}

export interface ServicesConfig {
  isEnabled: boolean;
  schedule: Schedule;
  fee?: FeeConfig;
  type: "fixed" | "manual" | "auto";
  maxDistanceKm?: number | null;
}


export interface OperationTiming {
  review: number;
  preparation: number;
  shipping: number;
}


export interface OrganizationSettings {
  services: {
    dineIn: Omit<ServicesConfig, 'schedule'>;
    delivery: ServicesConfig;
    pickUp: ServicesConfig;
  };
  orderManagement: string;
  includeTax: boolean;
  paymentMethods: PaymentMethods;
  operationTiming: OperationTiming;
  loyaltyProgram?: LoyaltyProgram;
  postOrderSurveyId?: string | null;
  drivers: string[];

}

export interface Organization {
  id: string;
  alias: string;
  title: string;
  plan: PlanType;
  industry: IndustryId;
  slogan?: string;
  logo?: string;
  isotype?: string;
  header?: string;
  categories?: OrgCategory[];
  createdAt: number;
  createdBy: string;
  lastUpdatedAt: number;
  lastUpdatedBy: string;
  tables?: Table[];
  orders?: Order[];
  waiters?: Waiter[];
  paidOrders?: Order[];
  access: Record<User["uid"], Access & User>;
  invitations?: Invitation[];
  content?: Content[] | Offer[];
  avatars?: Avatar[];
  primaryColor?: string;
  secondaryColor?: string;
  website?: Website["id"] | null;
  websites?: Website[];
  quotes?: Quote[];
  images?: string[];
  email: string;
  address: string;
  phone: number;
  whatsapp: number;
  latitude?: number;
  longitude?: number;
  settings: OrganizationSettings;
  mainBranch?: Branch["id"] | null;
  branches?: Branch[];
  //Agreements
  legal?: Legal;
  //Users
  users?: User[];
  // Social media
  facebook?: string;
  x?: string;
  instagram?: string;
  tiktok?: string;
  // SEO
  domain?: string;
  googlePlaceId?: string;
  googleDomainVerificationToken?: string;
  isDomainVerifiedByGoogle?: boolean;
  productAdoptionProfile?: ProductAdoptionProfile;
}

export interface FeaturesEnabled {
  hasCreatedMenu: boolean;
  hasCreatedContent: boolean;
  hasEnabledDelivery: boolean;
  hasEnabledLoyalty: boolean;
  hasConfiguredDishes: boolean;
  hasInitialCustomers: boolean;
  hasInvitedStaff?: boolean;
  // Banderas para ignorar pasos
  hasIgnoredStaff?: boolean;
  hasIgnoredDelivery?: boolean;
  hasIgnoredLoyalty?: boolean;
}

export interface ProductAdoptionProfile {
  lastUpdated: string;
  featuresEnabled: FeaturesEnabled;
}